/**
 * @fileoverview Shared slide-in detail body for report and dashboard definition grids across
 * `tenant-web` (`DashboardViewerPage`, `DashboardTemplates`, `DataReportLibrary`, `DataReportTemplates`)
 * and `system-web` (`SystemDashboardsPage`).
 * @module ui-admin/components/ReportDefinitionDetailPanel
 *
 * Responsibilities: - Render standard detail sections for a `ReportDefinitionLocal` row:
 *                     identity meta, status, scope, assignment (`AssignmentBadge`), system
 *                     library visibility (`VisibilityBadge`), widget count, and timestamps.
 *                   - Expose optional primary action (`runLabel` / `onRun`) injected by the host.
 * Dependencies:     - ../Badges (StatusBadge, AssignmentBadge, VisibilityBadge)
 *                   - @firesquirrel/local-db (ReportDefinitionLocal)
 *                   - @ui-common (formatDate)
 * Architecture:     - Presentation-only shared package component (DRY across tenant-web and system-web).
 *                     Zero Dexie or API access.
 * Notes:            - Extracted to `@firesquirrel/ui-admin` so both portals share a single implementation.
 */

import React from 'react';
import { StatusBadge, AssignmentBadge, VisibilityBadge } from '../Badges';
import type { ReportDefinitionLocal } from '@firesquirrel/local-db';
import { formatDate } from '../parcel-data-panel/sub-components';

function MetaRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-start gap-2 py-1">
      <span className="text-xs font-medium text-gray-500 w-28 shrink-0">{label}</span>
      <span className="text-xs text-gray-700 min-w-0">{children}</span>
    </div>
  );
}

const TYPE_LABELS: Record<string, string> = {
  report: 'Data Report',
  dashboard: 'Dashboard',
  document: 'Document',
};

/**
 * @contract ReportDefinitionDetailPanelProps
 * Purpose: Props for `ReportDefinitionDetailPanel`.
 * Fields:  definition, runLabel?, onRun?, showAssignment?
 * Rules:   Presentation-only; `definition` is passed from the local-first grid.
 * Sync:    N/A.
 */
export interface ReportDefinitionDetailPanelProps {
  definition: ReportDefinitionLocal;
  runLabel?: string;
  onRun?: () => void;
  showAssignment?: boolean;
}

/**
 * @component ReportDefinitionDetailPanel
 * Purpose:     Standard slide-in body for a report or dashboard definition row.
 * Props:       ReportDefinitionDetailPanelProps.
 * Behavior:    Displays metadata, assignment/visibility badges, and optional primary run/view button.
 * Data Source: props only (from local-first grid).
 * Constraints: presentation-only; no API/Dexie access.
 * Performance: stateless.
 */
export function ReportDefinitionDetailPanel({
  definition,
  runLabel,
  onRun,
  showAssignment = definition.type === 'dashboard' && definition.scope?.level !== 'system',
}: ReportDefinitionDetailPanelProps) {
  const isSystem = definition.scope?.level === 'system';

  return (
    <div className="px-4 py-4 space-y-4">
      {onRun && runLabel && (
        <button
          type="button"
          onClick={onRun}
          className="w-full flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium text-white bg-orange-600 hover:bg-orange-700 rounded-lg transition-colors"
        >
          {runLabel}
        </button>
      )}

      <div>
        <MetaRow label="Type">{TYPE_LABELS[definition.type] ?? definition.type}</MetaRow>
        <MetaRow label="Status">
          <StatusBadge status={definition.status} />
        </MetaRow>
        <MetaRow label="Scope">
          <span className="capitalize">{definition.scope?.level ?? 'department'}</span>
        </MetaRow>
        {showAssignment && (
          <MetaRow label="Assignment">
            <AssignmentBadge
              isPrimary={Boolean(definition.isDefault && definition.isInLibrary)}
              isInLibrary={Boolean(definition.isInLibrary)}
              order={definition.assignmentOrder}
            />
          </MetaRow>
        )}
        {isSystem && (
          <MetaRow label="Visibility">
            <VisibilityBadge
              isInLibrary={Boolean(definition.isInLibrary)}
              visibility={definition.visibility ?? 'all-departments'}
              tenantCount={definition.allowedTenantIds?.length ?? 0}
            />
          </MetaRow>
        )}
        {definition.dashboardConfig?.widgets && (
          <MetaRow label="Widgets">{definition.dashboardConfig.widgets.length}</MetaRow>
        )}
        {definition.createdBy && <MetaRow label="Created By">{definition.createdBy}</MetaRow>}
        <MetaRow label="Created">{formatDate(definition.createdAt)}</MetaRow>
        <MetaRow label="Modified">{formatDate(definition.updatedAt)}</MetaRow>
        {definition.derivedFrom && (
          <MetaRow label="Origin">Adopted from the system library</MetaRow>
        )}
      </div>

      {definition.description && (
        <div>
          <h4 className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 mb-1">
            Description
          </h4>
          <p className="text-xs text-gray-700 whitespace-pre-wrap">{definition.description}</p>
        </div>
      )}
    </div>
  );
}

export default ReportDefinitionDetailPanel;
